import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Residents = () => {
  const [residents, setResidents] = useState([]);
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedResident, setSelectedResident] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResidents();
  }, []);

  useEffect(() => {
    filterResidents();
  }, [residents, searchTerm, filterStatus]);

  const fetchResidents = async () => {
    try {
      const { data, error } = await supabase
        .from('residents')
        .select(`
          *,
          leases(
            *,
            units(unit_number, properties(name)),
            rent_payments(status, amount, due_date)
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setResidents(data || []);
    } catch (error) {
      console.error('Error fetching residents:', error);
    }
    setLoading(false);
  };

  const filterResidents = () => {
    let filtered = residents;
    
    if (searchTerm) {
      filtered = filtered.filter(resident =>
        `${resident.first_name} ${resident.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resident.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(resident => resident.status === filterStatus);
    }
    
    setFilteredResidents(filtered);
  };

  const viewResidentDetails = async (resident) => {
    try {
      const { data: maintenanceRequests } = await supabase
        .from('maintenance_requests')
        .select('*')
        .eq('resident_id', resident.id)
        .order('created_at', { ascending: false });

      const { data: messages } = await supabase
        .from('messages')
        .select('*')
        .eq('recipient_id', resident.user_id)
        .order('created_at', { ascending: false });

      setSelectedResident({
        ...resident,
        maintenanceRequests: maintenanceRequests || [],
        messages: messages || []
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching resident details:', error);
    }
  };

  const sendMessage = async (residentId, subject, content) => {
    try {
      await supabase.from('messages').insert({
        recipient_id: residentId,
        subject,
        content,
        channel: 'portal'
      });
      alert('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const getLeaseStatus = (resident) => {
    const activeLease = resident.leases?.find(lease => lease.status === 'active');
    if (!activeLease) return 'No Active Lease';
    
    const endDate = new Date(activeLease.lease_end);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'Lease Expired';
    if (daysUntilExpiry < 30) return `Expires in ${daysUntilExpiry} days`;
    return 'Active';
  };

  const getRentStatus = (resident) => {
    const activeLease = resident.leases?.find(lease => lease.status === 'active');
    if (!activeLease) return 'N/A';
    
    const pendingPayments = activeLease.rent_payments?.filter(p => p.status === 'pending') || [];
    const overduePayments = pendingPayments.filter(p => new Date(p.due_date) < new Date());
    
    if (overduePayments.length > 0) return 'Overdue';
    if (pendingPayments.length > 0) return 'Due';
    return 'Current';
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Loading residents...</h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2>Residents</h2>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search residents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', width: '200px' }}
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Resident</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Unit</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Lease Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Rent Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResidents.map(resident => {
                const activeLease = resident.leases?.find(lease => lease.status === 'active');
                const leaseStatus = getLeaseStatus(resident);
                const rentStatus = getRentStatus(resident);
                
                return (
                  <tr key={resident.id}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                      <div>
                        <strong>{resident.first_name} {resident.last_name}</strong>
                        <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{resident.email}</p>
                        <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{resident.phone}</p>
                      </div>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                      {activeLease ? (
                        <div>
                          <strong>{activeLease.units?.properties?.name}</strong>
                          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>Unit {activeLease.units?.unit_number}</p>
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>No active lease</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: leaseStatus.includes('Expires') ? '#fff3cd' : leaseStatus === 'Active' ? '#d4edda' : '#f8d7da',
                        color: leaseStatus.includes('Expires') ? '#856404' : leaseStatus === 'Active' ? '#155724' : '#721c24'
                      }}>
                        {leaseStatus}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: rentStatus === 'Overdue' ? '#f8d7da' : rentStatus === 'Due' ? '#fff3cd' : '#d4edda',
                        color: rentStatus === 'Overdue' ? '#721c24' : rentStatus === 'Due' ? '#856404' : '#155724'
                      }}>
                        {rentStatus}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: resident.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: resident.status === 'active' ? '#155724' : '#721c24'
                      }}>
                        {resident.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <button
                        onClick={() => viewResidentDetails(resident)}
                        className="btn btn-primary"
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredResidents.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              No residents found matching your criteria.
            </div>
          )}
        </div>

        {/* Resident Details Modal */}
        {showModal && selectedResident && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', maxWidth: '800px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>{selectedResident.first_name} {selectedResident.last_name}</h3>
                <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                  <h4>Contact Information</h4>
                  <p><strong>Email:</strong> {selectedResident.email}</p>
                  <p><strong>Phone:</strong> {selectedResident.phone}</p>
                  <p><strong>Status:</strong> {selectedResident.status}</p>
                  
                  <h4 style={{ marginTop: '20px' }}>Current Lease</h4>
                  {selectedResident.leases?.find(l => l.status === 'active') ? (
                    <div>
                      <p><strong>Property:</strong> {selectedResident.leases.find(l => l.status === 'active').units?.properties?.name}</p>
                      <p><strong>Unit:</strong> {selectedResident.leases.find(l => l.status === 'active').units?.unit_number}</p>
                      <p><strong>Rent:</strong> ${selectedResident.leases.find(l => l.status === 'active').rent_amount}</p>
                      <p><strong>Lease End:</strong> {new Date(selectedResident.leases.find(l => l.status === 'active').lease_end).toLocaleDateString()}</p>
                    </div>
                  ) : (
                    <p>No active lease</p>
                  )}
                </div>
                
                <div>
                  <h4>Recent Maintenance Requests</h4>
                  <div style={{ maxHeight: '150px', overflow: 'auto' }}>
                    {selectedResident.maintenanceRequests?.slice(0, 3).map(request => (
                      <div key={request.id} style={{ padding: '8px', background: '#f8f9fa', borderRadius: '4px', marginBottom: '8px' }}>
                        <strong>{request.title}</strong>
                        <p style={{ fontSize: '12px', color: '#666', margin: '2px 0' }}>{request.status} - {new Date(request.created_at).toLocaleDateString()}</p>
                      </div>
                    )) || <p>No maintenance requests</p>}
                  </div>
                  
                  <h4 style={{ marginTop: '20px' }}>Quick Actions</h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button 
                      className="btn btn-primary"
                      onClick={() => sendMessage(selectedResident.user_id, 'Message from Management', 'Hello, this is a message from property management.')}
                      style={{ fontSize: '12px' }}
                    >
                      Send Message
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '12px' }}>View Payments</button>
                    <button className="btn btn-secondary" style={{ fontSize: '12px' }}>View Documents</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Residents;